import { useQuery } from '@apollo/client';
import React, { PropsWithChildren, useContext, useMemo, useState } from 'react';
import {
  FranvaroReservForQuery,
  FranvaroReservForQueryVariables,
  FranvaroReservForQuery_db_SYSFranvaro_All,
} from '../../Components/FranvaroReservFor/Queries/_QueryTypes/FranvaroReservForQuery';
import franvaroReservForQuery from '../../Components/FranvaroReservFor/Queries/FranvaroReservForQuery.gql';
import { ActiveDatabaseContext } from '../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { RutinSettingsContext } from '../../../../../_Common/Components/RutinAvailabilityContext/RutinAvailabilityContext';
import { FranvaroContext, IFranvaroContext } from './FranvaroContext';
import { UserQuery_node_TEDSYSUser__Associations_Franvaro_All } from '../../Queries/_QueryTypes/UserQuery';
import FranvaroDialog from '../../Components/FranvaroDialog/FranvaroDialog';
import { EK_BOKFORDER_UTANORDNING_ACTIVE } from '../../../../../Ekonomi/Rutiner/Bokforder/Const/BokforderSysoptionConsts';
import sysoptionQuery from './Queries/SYSFranvaroSysoptionQuery.gql';
import {
  SYSFranvaroSysoptionQuery,
  SYSFranvaroSysoptionQueryVariables,
} from './Queries/_QueryTypes/SYSFranvaroSysoptionQuery';

interface IProps extends PropsWithChildren {
  routineNumber: number;
}

const FranvaroContextManager: React.FC<IProps> = props => {
  const { activeDatabase, userID } = useContext(ActiveDatabaseContext);
  const rutinSettingsContext = useContext(RutinSettingsContext);

  const [franvaroDialogOpen, setFranvaroDialogOpen] = useState<boolean>(false);
  const [registerFranvaroDialogOpen, setRegisterFranvaroDialogOpen] =
    useState<boolean>(false);
  const [franvaroReservID, setFranvaroReservID] = useState<string | null>(null);
  const [editFranvaro, setEditFranvaro] =
    useState<UserQuery_node_TEDSYSUser__Associations_Franvaro_All>();

  const dateString = new Date().toLocaleDateString('sv-SE');

  const {
    data: reservForData,
    loading: franvaroReservForQueryLoading,
    refetch,
  } = useQuery<FranvaroReservForQuery, FranvaroReservForQueryVariables>(
    franvaroReservForQuery,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        dbName: activeDatabase ?? '',
        routineID: rutinSettingsContext.activeRoutine?.routine.rutinID,
        userID: userID ?? '',
        date: dateString,
      },
      skip: !activeDatabase || !userID,
    }
  );

  const sysOptionsQuery = useQuery<
    SYSFranvaroSysoptionQuery,
    SYSFranvaroSysoptionQueryVariables
  >(sysoptionQuery, {
    variables: {
      dbName: activeDatabase ?? '',
      optionNames: [{ value: EK_BOKFORDER_UTANORDNING_ACTIVE }],
      routine: rutinSettingsContext.activeRoutine?.routine.rutinID ?? 0,
    },
    skip: !activeDatabase,
  });

  const sysoptions = useMemo(
    () => sysOptionsQuery.data?.db?.SYSOptions_All,
    [sysOptionsQuery.data?.db?.SYSOptions_All]
  );

  const franvaroReservForData: FranvaroReservForQuery_db_SYSFranvaro_All[] =
    useMemo(
      () => reservForData?.db?.SYSFranvaro_All ?? [],
      [reservForData?.db?.SYSFranvaro_All]
    );

  const franvaroValue: IFranvaroContext = useMemo(
    () => ({
      franvaroDialogOpen,
      editFranvaro,
      franvaroReservID,
      franvaroReservForData,
      franvaroReservForQueryLoading,
      franvaroReservForRefetch: refetch,
      registerFranvaroDialogOpen,
      setFranvaroDialogOpen,
      setEditFranvaro,
      setFranvaroReservID,
      setRegisterFranvaroDialogOpen,
      sysoptions,
    }),
    [
      franvaroDialogOpen,
      editFranvaro,
      franvaroReservID,
      franvaroReservForData,
      franvaroReservForQueryLoading,
      refetch,
      registerFranvaroDialogOpen,
      sysoptions,
    ]
  );

  return (
    <>
      <FranvaroContext.Provider value={franvaroValue}>
        {props.children}
        <FranvaroDialog routineNumber={props.routineNumber} />
      </FranvaroContext.Provider>
    </>
  );
};

export default FranvaroContextManager;
